import { useCallback, useEffect, useRef, useState } from 'react';

import type { SkillCommit } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

import type { ResourceKind } from './use-resource-tree';

type Status = 'idle' | 'loading' | 'success' | 'error';

const PAGE_SIZE = 25;

/**
 * Shared ViewModel for the revision-history pane used by both Skills and
 * Agents. Paginated `Load more`, tracks the currently-viewed ref so the
 * sibling file-browser hook can time-travel.
 */
export function useResourceHistory(
  kind: ResourceKind,
  resourceId: string | null,
  options: { enabled?: boolean } = {},
) {
  const apiClient = useApiClient();
  const { enabled = true } = options;
  const [commits, setCommits] = useState<SkillCommit[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [viewedRef, setViewedRef] = useState<string | null>(null);

  const generationRef = useRef(0);

  const fetchHistory = useCallback(
    (id: string, opts: { limit: number; offset: number }) =>
      kind === 'agent'
        ? apiClient.getAgentHistory(id, opts)
        : apiClient.getSkillHistory(id, opts),
    [apiClient, kind],
  );

  useEffect(() => {
    if (!resourceId || !enabled) {
      setCommits([]);
      setStatus('idle');
      setViewedRef(null);
      setHasMore(false);
      return;
    }
    const gen = ++generationRef.current;
    setStatus('loading');
    setViewedRef(null);
    fetchHistory(resourceId, { limit: PAGE_SIZE, offset: 0 })
      .then((data) => {
        if (gen !== generationRef.current) return;
        setCommits(data);
        setHasMore(data.length === PAGE_SIZE);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load history');
        setStatus('error');
      });
  }, [fetchHistory, resourceId, enabled]);

  const loadMore = useCallback(() => {
    if (!resourceId || !hasMore) return;
    const gen = generationRef.current;
    fetchHistory(resourceId, { limit: PAGE_SIZE, offset: commits.length })
      .then((more) => {
        if (gen !== generationRef.current) return;
        setCommits((prev) => [...prev, ...more]);
        setHasMore(more.length === PAGE_SIZE);
      })
      .catch((err: unknown) => {
        if (gen !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load more history');
      });
  }, [fetchHistory, resourceId, commits.length, hasMore]);

  const viewAtRef = useCallback((ref: string | null) => {
    setViewedRef(ref);
  }, []);

  return { commits, status, error, hasMore, loadMore, viewedRef, viewAtRef };
}
